import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Field } from "../../components/Field";
import { Skeleton } from "../../components/Spinner";
import { apiGet } from "../../lib/customFetch";
import {
  AdminProblem,
  beskrivAdminProblem,
  lokaltValideringsproblem,
  type AdminProblemValue,
} from "./AdminProblem";
import { formatMinutter } from "./format";
import styles from "./ReportView.module.css";

interface RapportTotaler {
  normMinutter: number | null;
  bruttoMinutter: number;
  pauseMinutter: number;
  faktiskMinutter: number;
  overtidMinutter: number | null;
}

interface RapportProjekt {
  projektId: string | null;
  navn: string | null;
  kundenavn: string | null;
  faktiskMinutter: number;
}

interface RapportMedarbejder {
  id: string;
  navn: string;
  mode: "stempel" | "standard";
  harRegistreringer: boolean;
  totaler: RapportTotaler;
  projektfordeling: RapportProjekt[];
  fravaer: Array<{
    dato: string;
    type: "ferie" | "sygdom" | "fri";
    note: string | null;
  }>;
}

interface RapportSvar {
  fra: string;
  til: string;
  projekterAktiv: boolean;
  medarbejdere: RapportMedarbejder[];
}

interface Filter {
  fra: string;
  til: string;
  userId: string;
}

type ViewState =
  | { status: "loading" }
  | { status: "ok"; data: RapportSvar }
  | { status: "failed"; problem: AdminProblemValue };

function inputDato(dato: Date): string {
  const år = dato.getFullYear();
  const måned = String(dato.getMonth() + 1).padStart(2, "0");
  const dag = String(dato.getDate()).padStart(2, "0");
  return `${String(år)}-${måned}-${dag}`;
}

function startFilter(): Filter {
  const nu = new Date();
  return {
    fra: inputDato(new Date(nu.getFullYear(), nu.getMonth(), 1)),
    til: inputDato(nu),
    userId: "",
  };
}

function datoTekst(dato: string): string {
  return new Intl.DateTimeFormat("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(`${dato}T12:00:00Z`));
}

function minutTekst(minutter: number | null): string {
  return minutter === null ? "Ikke sat" : formatMinutter(minutter);
}

function rapportUrl(filter: Filter): string {
  const query = new URLSearchParams({ fra: filter.fra, til: filter.til });
  if (filter.userId !== "") query.set("userId", filter.userId);
  return `/api/clockin/admin/report?${query.toString()}`;
}

export function ReportView() {
  const [filter, setFilter] = useState<Filter>(startFilter);
  const [aktivtFilter, setAktivtFilter] = useState<Filter>(startFilter);
  const [state, setState] = useState<ViewState>({ status: "loading" });
  const [muligheder, setMuligheder] = useState<Array<{ id: string; navn: string }>>(
    [],
  );
  const requestId = useRef(0);

  const hent = useCallback((valg: Filter) => {
    const id = ++requestId.current;
    setState({ status: "loading" });
    apiGet<RapportSvar>(rapportUrl(valg))
      .then((data) => {
        if (id !== requestId.current) return;
        if (valg.userId === "") {
          setMuligheder(data.medarbejdere.map(({ id: userId, navn }) => ({ id: userId, navn })));
        }
        setState({ status: "ok", data });
      })
      .catch((error: unknown) => {
        if (id !== requestId.current) return;
        setState({
          status: "failed",
          problem: beskrivAdminProblem(error, "hente rapporten"),
        });
      });
  }, []);

  useEffect(() => {
    hent(aktivtFilter);
  }, [aktivtFilter, hent]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (filter.fra > filter.til) {
      setState({
        status: "failed",
        problem: lokaltValideringsproblem("Fra-dato skal ligge før eller på til-dato."),
      });
      return;
    }
    setAktivtFilter({ ...filter });
  };

  return (
    <section className={styles.root} aria-labelledby="rapport-heading">
      <div className={styles.headingRow}>
        <div>
          <h2 id="rapport-heading" className={styles.heading}>Løngrundlag</h2>
          <p className={styles.intro}>
            Norm, faktisk tid, pause og overtid fra det samme grundlag som uge og eksport.
          </p>
        </div>
        <Button className={styles.printButton} variant="secondary" onClick={() => window.print()}>
          Print rapport
        </Button>
      </div>

      <form className={styles.filters} onSubmit={submit}>
        <Field
          label="Fra dato"
          type="date"
          required
          value={filter.fra}
          onChange={(event) => setFilter((current) => ({ ...current, fra: event.target.value }))}
        />
        <Field
          label="Til dato"
          type="date"
          required
          value={filter.til}
          onChange={(event) => setFilter((current) => ({ ...current, til: event.target.value }))}
        />
        <label className={styles.selectField}>
          <span>Medarbejder</span>
          <select
            value={filter.userId}
            onChange={(event) => setFilter((current) => ({ ...current, userId: event.target.value }))}
          >
            <option value="">Alle medarbejdere</option>
            {muligheder.map((medarbejder) => (
              <option key={medarbejder.id} value={medarbejder.id}>
                {medarbejder.navn}
              </option>
            ))}
          </select>
        </label>
        <Button className={styles.showButton} type="submit">Vis rapport</Button>
      </form>

      {state.status === "loading" && <ReportSkeleton />}
      {state.status === "failed" && (
        <AdminProblem problem={state.problem} onAction={() => hent(aktivtFilter)} />
      )}
      {state.status === "ok" && (
        <ReportContent data={state.data} />
      )}
    </section>
  );
}

function ReportSkeleton() {
  return (
    <div className={styles.list} aria-hidden="true">
      {[0, 1].map((item) => (
        <div key={item} className={styles.employeeCard}>
          <Skeleton width="12rem" height="1.5rem" />
          <div className={styles.metrics}>
            {[0, 1, 2, 3].map((metric) => (
              <Skeleton key={metric} height="4.5rem" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportContent({ data }: { data: RapportSvar }) {
  const harData = data.medarbejdere.some((medarbejder) => {
    const totaler = medarbejder.totaler;
    return (
      medarbejder.harRegistreringer ||
      (totaler.normMinutter ?? 0) > 0 ||
      totaler.bruttoMinutter > 0 ||
      totaler.pauseMinutter > 0 ||
      totaler.faktiskMinutter > 0 ||
      (totaler.overtidMinutter ?? 0) !== 0 ||
      medarbejder.fravaer.length > 0 ||
      medarbejder.projektfordeling.some((projekt) => projekt.faktiskMinutter > 0)
    );
  });

  return (
    <div>
      <p className={styles.period}>
        Periode: <strong>{datoTekst(data.fra)} – {datoTekst(data.til)}</strong>
      </p>
      {harData ? (
        <div className={styles.list}>
          {data.medarbejdere.map((medarbejder) => (
            <EmployeeReport
              key={medarbejder.id}
              medarbejder={medarbejder}
              projekterAktiv={data.projekterAktiv}
            />
          ))}
        </div>
      ) : (
        <EmptyState
          title="Ingen data i perioden"
          description="Der er ingen registreret tid, normtid eller fravær i den valgte periode."
        />
      )}
    </div>
  );
}

function EmployeeReport({
  medarbejder,
  projekterAktiv,
}: {
  medarbejder: RapportMedarbejder;
  projekterAktiv: boolean;
}) {
  const normUkendt = medarbejder.totaler.normMinutter === null;
  return (
    <article className={styles.employeeCard}>
      <header className={styles.employeeHeader}>
        <h3>{medarbejder.navn}</h3>
        <span className={styles.mode}>
          {medarbejder.mode === "standard" ? "Standarddag" : "Stempel"}
        </span>
      </header>
      <dl className={styles.metrics}>
        <Metric label="Normtid" value={minutTekst(medarbejder.totaler.normMinutter)} unknown={normUkendt} />
        <Metric label="Faktisk tid" value={minutTekst(medarbejder.totaler.faktiskMinutter)} />
        <Metric label="Pause" value={minutTekst(medarbejder.totaler.pauseMinutter)} />
        <Metric label="Overtid" value={minutTekst(medarbejder.totaler.overtidMinutter)} unknown={normUkendt} />
      </dl>
      {normUkendt && (
        <p className={styles.notice} role="note">
          Normtid er ikke konfigureret. Derfor beregnes overtid ikke.
        </p>
      )}
      {medarbejder.fravaer.length > 0 && (
        <div className={styles.absence}>
          <h4>Fravær</h4>
          <ul>
            {medarbejder.fravaer.map((dag) => (
              <li key={dag.dato}>
                <strong>{datoTekst(dag.dato)}</strong>
                {` · ${{ ferie: "Ferie", sygdom: "Sygdom", fri: "Fri" }[dag.type]}`}
                {dag.note ? ` · ${dag.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {projekterAktiv && medarbejder.mode === "stempel" && medarbejder.projektfordeling.length > 0 && (
        <div className={styles.projects}>
          <h4>Projektfordeling efter pause</h4>
          <table>
            <thead>
              <tr><th scope="col">Projekt</th><th scope="col">Kunde</th><th scope="col">Tid</th></tr>
            </thead>
            <tbody>
              {medarbejder.projektfordeling.map((projekt) => (
                <tr key={projekt.projektId ?? "uden-projekt"}>
                  <td>{projekt.navn ?? "Uden projekt"}</td>
                  <td>{projekt.kundenavn ?? "—"}</td>
                  <td>{formatMinutter(projekt.faktiskMinutter)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </article>
  );
}

function Metric({ label, value, unknown = false }: { label: string; value: string; unknown?: boolean }) {
  return (
    <div className={unknown ? styles.metricUnknown : undefined}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
